import React, { useEffect } from 'react';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { core_apimessages_CartItemAttributes as CartItemAttributes } from '../schemas/rql';
import LoggedOutApplePayButton from './logged_out_apple_pay_button';
import LoggedInApplePayButton from './logged_in_apple_pay_button';
import BuyListingNowApplePayButton from './buy_listing_now_apple_pay_button';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { useApplePay } from './apple_pay_hook';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { CheckoutItemPageReturnErrorCallback } from './checkout_item_page_return_layout';

interface Props {
  listingCurrencyCode: string;
  paymentMethodsConfig: string;
  payButtonType?: 'checkout' | 'plain';
  cartItemAttributes?: CartItemAttributes[];
  isBuyListingNow?: boolean;
  listingId?: string;
  setIsLoading?: (loading: boolean) => any;
  isDisabled?: boolean;
  onError?: CheckoutItemPageReturnErrorCallback;
}

export function useCheckApplePaySupport(paymentMethodsConfig, listingCurrencyCode) {
  const [isSupported, setIsSupported] = React.useState(false);
  const [isSupportCheckLoading, setIsSupportCheckLoading] = React.useState(true);
  const user = useUser();
  const { isAvailable, loading } = useApplePay({
    paymentMethodsConfig,
    listingCurrencyCode,
  });

  useEffect(() => {
    async function checkApplePaySupport() {
      if (['US', 'GB', 'CA'].includes(user.countryCode)) {
        setIsSupported(isAvailable);
        setIsSupportCheckLoading(false);
      } else if (window.ApplePaySession) {
        const { paymentMethods } = JSON.parse(paymentMethodsConfig);
        const { merchantId } = paymentMethods.find((method) => method.type === 'applepay').configuration;
        setIsSupported(await ApplePaySession.canMakePaymentsWithActiveCard(merchantId));
        setIsSupportCheckLoading(false);
      } else {
        setIsSupportCheckLoading(false);
      }
    }

    checkApplePaySupport();
  }, [paymentMethodsConfig, user.countryCode, isAvailable]);

  return [isSupported, isSupportCheckLoading || loading];
}

export default function ApplePayButton(props: Props) {
  const {
    paymentMethodsConfig,
    listingCurrencyCode,
    payButtonType,
    cartItemAttributes,
    setIsLoading,
    isDisabled,
    onError,
    isBuyListingNow,
  } = props;

  const user = useUser();

  const [isAvailable, loading] = useCheckApplePaySupport(paymentMethodsConfig, listingCurrencyCode);

  const { componentName } = applePayViewTracking({ user, isBuyListingNow });

  useViewTracking({
    componentName,
    eventName: MParticleEventName.ApplePayOffered,
    applePayAvailableForDevice: isAvailable,
  }, !loading);

  if (!isAvailable) {
    return null;
  }

  if (props.isBuyListingNow) {
    return (
      <BuyListingNowApplePayButton
        data-buy-listing-now-apple-pay-button
        listingId={props.listingId}
        paymentMethodsConfig={paymentMethodsConfig}
        listingCurrencyCode={listingCurrencyCode}
        setIsLoading={setIsLoading}
        isDisabled={isDisabled}
        onError={onError}
      />
    );
  }

  if (user.loggedOut) {
    return (
      <LoggedOutApplePayButton
        data-logged-out-apple-pay-button
        paymentMethodsConfig={paymentMethodsConfig}
        listingCurrencyCode={listingCurrencyCode}
        cartItemAttributes={cartItemAttributes}
        payButtonType={payButtonType}
      />
    );
  }

  return (
    <LoggedInApplePayButton
      data-logged-in-apple-pay-button
      paymentMethodsConfig={paymentMethodsConfig}
      listingCurrencyCode={listingCurrencyCode}
      payButtonType={payButtonType}
    />
  );
}

function applePayViewTracking({ user, isBuyListingNow }: { user: Partial<IUser>, isBuyListingNow: boolean, }) {
  if (isBuyListingNow) {
    return {
      componentName: 'BuyListingNowApplePayButton',
    };
  } else if (user.loggedOut) {
    return {
      componentName: 'LoggedOutApplePayButton',
    };
  } else {
    return {
      componentName: 'ApplePayButton',
    };
  }
}
